export type StoreCategory = "shoes" | "glasses" | "contact_lenses" | "default";

export interface UploadInstructions {
  category: StoreCategory;
  title: string;
  subtitle: string;
  uploadLabel: string;
  uploadHint: string;
  instructionsHeading: string;
  tips: string[];
}

export const STORE_INSTRUCTIONS: Record<StoreCategory, UploadInstructions> = {
  shoes: {
    category: "shoes",
    title: "VIRTUAL TRY-ON",
    subtitle: "Try SnapStyle's AI-Powered virtual shoe try-on today",
    uploadLabel: "Upload shoe photo",
    uploadHint: "Choose a clear photo showing your feet or lower legs for shoe try-on",
    instructionsHeading: "Photo instructions for shoe try-on:",
    tips: [
      "Stand on a flat, even surface with good lighting",
      "Ensure both feet, ankles, and footwear area are clearly in the frame",
      "Roll up pants or wear shorts so your feet and ankles are not covered",
      "Take the photo from a standing perspective or front-facing lower-body angle",
    ],
  },
  glasses: {
    category: "glasses",
    title: "VIRTUAL TRY-ON",
    subtitle: "Try SnapStyle's AI-Powered virtual spectacles try-on today",
    uploadLabel: "Upload portrait photo",
    uploadHint: "Choose a clear, front-facing portrait photo for spectacles try-on",
    instructionsHeading: "Photo instructions for spectacles try-on:",
    tips: [
      "Look directly into the camera with your head straight and level",
      "Remove any existing spectacles or sunglasses before taking the photo",
      "Ensure your face, eyes, and eyebrows are well-lit and clearly visible",
      "Keep hair away from your face and temples so the frame fits naturally",
    ],
  },
  contact_lenses: {
    category: "contact_lenses",
    title: "VIRTUAL TRY-ON",
    subtitle: "Try SnapStyle's AI-Powered virtual contact lens try-on today",
    uploadLabel: "Upload eye photo",
    uploadHint: "Choose a clear, well-lit eye photo or portrait for contact lens try-on",
    instructionsHeading: "Photo instructions for contact lens try-on:",
    tips: [
      "Look directly into the camera with your eyes wide open and relaxed",
      "Remove any glasses or current contact lenses before taking the photo",
      "Ensure bright, even lighting on your eyes without flash glare or shadows",
      "Keep hair and eyelashes from obstructing your iris and pupils",
    ],
  },
  default: {
    category: "default",
    title: "VIRTUAL TRY-ON",
    subtitle: "Try SnapStyle's AI-Powered virtual<br/>experience today",
    uploadLabel: "Upload photo",
    uploadHint: "Choose a front-facing photo for the best virtual try-on results",
    instructionsHeading: "",
    tips: [],
  },
};

/**
 * Cleans and normalizes a domain string by stripping protocol, path, port, and www prefix.
 */
export function normalizeDomain(domain?: string): string {
  if (!domain) return "";
  let clean = domain.trim().toLowerCase();
  clean = clean.replace(/^[a-z]+:\/\//i, "");
  clean = clean.split("/")[0] ?? "";
  clean = clean.split("?")[0] ?? "";
  clean = clean.split("#")[0] ?? "";
  clean = clean.split(":")[0] ?? "";
  clean = clean.replace(/^www\./, "");
  return clean;
}

/**
 * Determines the store category strictly based on domain conditions.
 */
export function getStoreCategory(shopDomain?: string): StoreCategory {
  let domain = normalizeDomain(shopDomain);
  if (!domain && typeof window !== "undefined" && window.location) {
    domain = normalizeDomain(window.location.hostname || window.location.host);
  }

  if (!domain) return "default";

  if (
    domain === "zeeki.pk" ||
    domain.endsWith(".zeeki.pk") ||
    domain.includes("zeeki.pk") ||
    domain === "zeeki"
  ) {
    return "shoes";
  }

  if (
    domain === "mmspecs.com" ||
    domain.endsWith(".mmspecs.com") ||
    domain.includes("mmspecs.com") ||
    domain === "mmspecs"
  ) {
    return "glasses";
  }

  if (
    domain === "kiyowoeyes.com" ||
    domain.endsWith(".kiyowoeyes.com") ||
    domain.includes("kiyowoeyes.com") ||
    domain === "kiyowoeyes"
  ) {
    return "contact_lenses";
  }

  return "default";
}

/**
 * Returns the upload instructions configuration matching the given domain or category.
 */
export function getUploadInstructions(
  shopDomainOrCategory?: string
): UploadInstructions {
  if (
    shopDomainOrCategory === "shoes" ||
    shopDomainOrCategory === "glasses" ||
    shopDomainOrCategory === "contact_lenses" ||
    shopDomainOrCategory === "default"
  ) {
    return STORE_INSTRUCTIONS[shopDomainOrCategory];
  }

  const category = getStoreCategory(shopDomainOrCategory);
  return STORE_INSTRUCTIONS[category];
}
